import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { useFolderVault, useVaultActivity } from '@/hooks/useVault';
import {
  X,
  Shield,
  Upload,
  Clock,
  RefreshCcw,
  History,
  FileText,
  Calendar,
  Settings,
  Scale,
  ExternalLink,
  HelpCircle,
  Users,
  Eye,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { VaultSyncTab } from './VaultSyncTab';
import { VaultAutoDepositTab } from './VaultAutoDepositTab';
import { VaultManagementTab } from './VaultManagementTab';
import { FolderMembersTab } from './FolderMembersTab';
// import { VaultListTab } from './VaultListTab';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

interface VaultDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  folderUuid: string;
  folderName: string;
  folderEmail?: string;
  domainUuid?: string;
  onDataRefresh?: () => void;
}

interface ActivityItem {
  uuid: string;
  type: string;
  originalFilename: string;
  originalSize: number;
  createdAt: string;
  period?: { year: number; month: number };
  employeeNumber?: string;
}

/**
 * VaultDrawer - Panneau latéral pour la gestion des coffres-forts
 *
 * Pour les gestionnaires (CREATOR) :
 * - Affiche uniquement l'historique des dépôts (métadonnées sans contenu)
 * - Permet de configurer la synchronisation manuelle et automatique
 * - N'affiche PAS le contenu des documents
 *
 * Note: L'accès au contenu des documents est réservé aux employés (OWNER)
 * via leur interface dédiée (/my-vaults)
 */
export function VaultDrawer({
  isOpen,
  onClose,
  folderUuid,
  folderName,
  folderEmail,
  domainUuid,
  onDataRefresh,
}: VaultDrawerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('management');

  const {
    data: folderVault,
    isLoading: isLoadingVault,
    refetch: refetchVault,
  } = useFolderVault(folderUuid, isOpen && !!folderUuid);

  // Récupérer l'historique des dépôts
  const {
    data: activityData,
    isLoading: isLoadingActivity,
    refetch: refetchActivity,
  } = useVaultActivity(folderVault?.uuid || '', isOpen && !!folderVault?.uuid);

  const handleClose = () => {
    setActiveTab('management');
    onClose();
  };

  const handleRefresh = () => {
    refetchVault();
    refetchActivity();
    onDataRefresh?.();
  };

  // const handleVaultCreated = () => {
  //   refetchVault();
  //   refetchActivity();
  //   onDataRefresh?.();
  // };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getDocumentTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      payslip: 'Bulletin de paie',
      contract: 'Contrat',
      certificate: 'Attestation',
      annual_summary: 'Récapitulatif annuel',
      tax_document: 'Document fiscal',
      other: 'Autre',
    };
    return labels[type] || type;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-neutral-900/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-full max-w-3xl bg-white shadow-xl transition-transform">
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-neutral-200 p-6">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-100">
                <Shield className="h-6 w-6 text-primary-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-neutral-900">
                  {t('vault.drawer.title', 'Coffre-Fort Numérique')}
                </h2>
                <p className="text-sm text-neutral-500">{folderName}</p>
              </div>
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className='flex items-center gap-2'>
                <button
                  onClick={() => {
                    handleClose();
                    navigate('/vault-help');
                  }}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
                  title={t('vault.help.title', 'Aide')}
                >
                  <HelpCircle className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setActiveTab('compliance')}
                  className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${activeTab === 'compliance' ? 'bg-primary-100 text-primary-600' : 'text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600'}`}
                  title={t('vault.drawer.tabs.compliance', 'Conformité')}
                >
                  <Scale className="h-4 w-4" />
                </button>
                <button
                  onClick={handleRefresh}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
                  title={t('common.refresh', 'Actualiser')}
                >
                  <RefreshCcw className="h-4 w-4" />
                </button>
                <button
                  onClick={handleClose}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              {folderVault && (
                <button
                  onClick={() => {
                    handleClose();
                    navigate(`/vault/${folderVault.uuid}`);
                  }}
                  className="flex items-center gap-1.5 rounded-lg bg-primary-50 border border-primary-200 px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-100"
                  title={t('vault.drawer.accessVault', 'Accéder au coffre-fort')}
                >
                  <Eye className="h-3.5 w-3.5" />
                  {t('vault.drawer.accessVault', 'Accéder au coffre')}
                </button>
              )}
            </div>
          </div>

          {/* Tabs - Vue gestionnaire simplifiée */}
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="flex flex-1 flex-col overflow-hidden"
          >
            <div className="px-6 pt-2 overflow-x-auto">
              <TabsList className="mb-4 inline-flex w-max justify-start">
                <TabsTrigger value="management" className="gap-2">
                  <Settings className="h-4 w-4" />
                  {t('vault.drawer.tabs.management', 'Gestion')}
                </TabsTrigger>
                <TabsTrigger value="history" className="gap-2">
                  <History className="h-4 w-4" />
                  {t('vault.drawer.tabs.history', 'Historique')}
                </TabsTrigger>
                <TabsTrigger value="manualDeposit" className="gap-2">
                  <Upload className="h-4 w-4" />
                  {t('vault.drawer.tabs.manualDeposit', 'Dépôt Manuel')}
                </TabsTrigger>
                <TabsTrigger value="autoDeposit" className="gap-2">
                  <Clock className="h-4 w-4" />
                  {t('vault.drawer.tabs.autoDeposit', 'Dépôt Auto')}
                </TabsTrigger>
                <TabsTrigger value="members" className="gap-2">
                  <Users className="h-4 w-4" />
                  {t('vault.drawer.tabs.members', 'Membres')}
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Tab Contents */}
            <div className="flex-1 overflow-y-auto">
              <TabsContent value="management" className="h-full m-0">
                <VaultManagementTab
                  folderUuid={folderUuid}
                  folderName={folderName}
                  folderEmail={folderEmail}
                  folderVault={folderVault}
                  isLoadingVault={isLoadingVault}
                  onVaultCreated={handleRefresh}
                />
              </TabsContent>

              {/* Onglet Historique - Vue des dépôts sans accès au contenu */}
              <TabsContent value="history" className="h-full m-0">
                <div className="p-6">
                  {!folderVault ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <Shield className="h-12 w-12 text-neutral-300 mb-4" />
                      <p className="text-neutral-600 mb-2">
                        {t('vault.drawer.noVault', 'Aucun coffre-fort créé')}
                      </p>
                      <p className="text-sm text-neutral-500">
                        {t(
                          'vault.drawer.createVaultHint',
                          'Utilisez l\'onglet Synchro pour créer un coffre-fort et y déposer des documents.'
                        )}
                      </p>
                    </div>
                  ) : isLoadingActivity ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
                    </div>
                  ) : !activityData?.items?.length ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <FileText className="h-12 w-12 text-neutral-300 mb-4" />
                      <p className="text-neutral-600 mb-2">
                        {t('vault.drawer.noActivity', 'Aucun document déposé')}
                      </p>
                      <p className="text-sm text-neutral-500">
                        {t(
                          'vault.drawer.noActivityHint',
                          'Les documents apparaîtront ici après une synchronisation.'
                        )}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-medium text-neutral-700">
                          {t('vault.drawer.recentDeposits', 'Dépôts récents')}
                        </h3>
                        <span className="text-xs text-neutral-500">
                          {activityData.total}{' '}
                          {t('vault.drawer.documentsTotal', 'document(s)')}
                        </span>
                      </div>

                      {activityData.items.map((item: ActivityItem) => (
                        <div
                          key={item.uuid}
                          className="flex items-start gap-3 p-3 rounded-lg border border-neutral-200 bg-neutral-50"
                        >
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white border border-neutral-200">
                            <FileText className="h-5 w-5 text-neutral-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-neutral-900 truncate">
                              {item.originalFilename}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-700">
                                {getDocumentTypeLabel(item.type)}
                              </span>
                              <span className="text-xs text-neutral-500">
                                {formatFileSize(item.originalSize)}
                              </span>
                              {item.period && (
                                <span className="text-xs text-neutral-500 flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  {item.period.month}/{item.period.year}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-neutral-400 mt-1">
                              {formatDistanceToNow(new Date(item.createdAt), {
                                addSuffix: true,
                                locale: fr,
                              })}
                            </p>
                          </div>
                        </div>
                      ))}

                      {/* Note sur l'accès au contenu */}
                      <div className="mt-6 p-4 rounded-lg bg-amber-50 border border-amber-200">
                        <p className="text-sm text-amber-800">
                          <strong>{t('vault.drawer.accessNote', 'Note :')}</strong>{' '}
                          {t(
                            'vault.drawer.accessNoteText',
                            "En tant que gestionnaire, vous pouvez voir l'historique des dépôts mais pas leur contenu. L'accès aux documents est réservé à l'employé propriétaire du coffre-fort."
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="manualDeposit" className="h-full m-0">
                <VaultSyncTab
                  folderUuid={folderUuid}
                  folderName={folderName}
                  domainUuid={domainUuid}
                  folderVault={folderVault}
                />
              </TabsContent>

              <TabsContent value="autoDeposit" className="h-full m-0">
                <VaultAutoDepositTab
                  folderUuid={folderUuid}
                  folderName={folderName}
                  domainUuid={domainUuid}
                  folderVault={folderVault}
                />
              </TabsContent>

              <TabsContent value="members" className="h-full m-0">
                {folderVault ? (
                  <FolderMembersTab vaultId={folderVault.uuid} />
                ) : (
                  <div className="flex flex-col items-center justify-center p-12 text-center">
                    <Shield className="mb-4 h-12 w-12 text-neutral-300" />
                    <p className="text-neutral-600 mb-2">
                      {t('vault.members.noVaultYet', 'Aucun coffre-fort créé')}
                    </p>
                    <p className="text-sm text-neutral-500">
                      {t(
                        'vault.members.createVaultFirst',
                        "Activez d'abord le coffre-fort dans l'onglet Gestion pour pouvoir inviter des membres."
                      )}
                    </p>
                  </div>
                )}
              </TabsContent>

              {/* Onglet Conformité - Documentation légale */}
              <TabsContent value="compliance" className="h-full m-0">
                <div className="p-6">
                  <div className="mb-6 rounded-xl border border-primary-200 bg-gradient-to-br from-primary-50 to-white p-5">
                    <div className="flex items-start gap-4">
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary-100">
                        <Scale className="h-5 w-5 text-primary-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-neutral-900">
                          {t('vault.compliance.summary.title', 'Cadre légal du coffre-fort')}
                        </h3>
                        <p className="mt-1 text-sm text-neutral-600">
                          {t(
                            'vault.compliance.summary.description',
                            'Les fonctions observées sont distinguées des garanties contractuelles restant à établir.'
                          )}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Points clés de conformité */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-neutral-700">
                      {t('vault.compliance.drawer.keyPoints', 'Points clés de conformité')}
                    </h4>

                    <div className="rounded-lg border border-success-200 bg-success-50 p-3">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-success-600" />
                        <span className="text-sm font-medium text-success-700">
                          {t('vault.compliance.summary.retention', 'Conservation 50 ans minimum')}
                        </span>
                      </div>
                      <p className="mt-1 ml-6 text-xs text-success-600">
                        {t('vault.compliance.drawer.retentionDesc', "Conforme à l'Art. D3243-8 du Code du travail")}
                      </p>
                    </div>

                    <div className="rounded-lg border border-success-200 bg-success-50 p-3">
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-success-600" />
                        <span className="text-sm font-medium text-success-700">
                          {t('vault.compliance.summary.encryption', 'Chiffrement AES-256-GCM')}
                        </span>
                      </div>
                      <p className="mt-1 ml-6 text-xs text-success-600">
                        {t('vault.compliance.drawer.encryptionDesc', 'Standard de chiffrement gouvernemental')}
                      </p>
                    </div>

                    <div className="rounded-lg border border-success-200 bg-success-50 p-3">
                      <div className="flex items-center gap-2">
                        <History className="h-4 w-4 text-success-600" />
                        <span className="text-sm font-medium text-success-700">
                          {t('vault.compliance.summary.audit', "Piste d'audit immuable")}
                        </span>
                      </div>
                      <p className="mt-1 ml-6 text-xs text-success-600">
                        {t('vault.compliance.drawer.auditDesc', 'Chaînage cryptographique vérifiable')}
                      </p>
                    </div>

                    <div className="rounded-lg border border-success-200 bg-success-50 p-3">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-success-600" />
                        <span className="text-sm font-medium text-success-700">
                          {t('vault.compliance.drawer.timestamp', 'Horodatage technique')}
                        </span>
                      </div>
                      <p className="mt-1 ml-6 text-xs text-success-600">
                        {t('vault.compliance.drawer.timestampDesc', 'Preuve légale de dépôt')}
                      </p>
                    </div>
                  </div>

                  {/* Références légales */}
                  <div className="mt-6">
                    <h4 className="text-sm font-medium text-neutral-700 mb-2">
                      {t('vault.compliance.drawer.legalRefs', 'Références légales')}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-600">
                        Loi El Khomri (2016)
                      </span>
                      <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-600">
                        Décret n° 2016-1762
                      </span>
                      <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-600">
                        RGPD
                      </span>
                      <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-600">
                        eIDAS
                      </span>
                    </div>
                  </div>

                  {/* Lien vers la documentation complète */}
                  <div className="mt-6 rounded-lg border border-primary-200 bg-primary-50 p-4">
                    <p className="text-sm text-primary-700 mb-3">
                      {t(
                        'vault.compliance.drawer.fullDocHint',
                        'Consultez notre documentation complète pour comprendre en détail comment nous respectons chaque exigence légale.'
                      )}
                    </p>
                    <button
                      onClick={() => {
                        handleClose();
                        navigate('/vault-compliance');
                      }}
                      className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700"
                    >
                      {t('vault.compliance.drawer.viewFullDoc', 'Voir la documentation complète')}
                      <ExternalLink className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
